import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Languages } from 'lucide-react';
import { LOCALE_NAMES, type Locale } from '@zt/i18n';
import { Badge, Button, Dialog, Field, Select } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useT } from '../lib/i18n';
import type { ContentLocales } from '../lib/types';

/**
 * Çok dilli içerik (ADR-039): mağazanın içerik dilleri pazar dilleridir. Mağazanın tek dili varsa dil
 * seçici ve "Çeviri ekle" gösterilmez; ekran tek dilli mağazada sade kalır.
 */

/** Oturum içinde mağaza başına seçilen dil (listeye dönünce aynı dil açılır; tarayıcıya yazılmaz). */
const remembered = new Map<string, string>();

export const localeName = (l: string) => LOCALE_NAMES[l as Locale] ?? l.toUpperCase();

export function rememberLocale(storeId: string, locale: string) {
  remembered.set(storeId, locale);
}

export function useContentLocale(storeId: string | null | undefined) {
  const q = useQuery({
    queryKey: ['content-locales', storeId],
    queryFn: () => api.get<ContentLocales>(`/v1/admin/content-locales?storeId=${storeId}`),
    enabled: !!storeId,
  });
  const [choice, setChoice] = useState<string | undefined>(() =>
    storeId ? remembered.get(storeId) : undefined,
  );
  useEffect(() => setChoice(storeId ? remembered.get(storeId) : undefined), [storeId]);
  const locales = q.data?.locales ?? [];
  const locale = choice && locales.includes(choice as never) ? choice : (q.data?.defaultLocale ?? null);
  return {
    locales: locales as string[],
    locale,
    setLocale: (l: string) => {
      if (storeId) remembered.set(storeId, l);
      setChoice(l);
    },
  };
}

/** İçerik dili süzgeci (yalnızca mağazanın birden fazla dili varsa). */
export function LocalePicker({
  locales,
  locale,
  onChange,
}: {
  locales: string[];
  locale: string | null;
  onChange: (l: string) => void;
}) {
  const t = useT();
  if (locales.length < 2 || !locale) return null;
  return (
    <Field label={t('admin.translations.locale')} help={<HelpTip k="content.locale" />} className="max-w-xs">
      {(p) => (
        <Select {...p} value={locale} onChange={(e) => onChange(e.target.value)}>
          {locales.map((l) => (
            <option key={l} value={l}>
              {localeName(l)}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

type EditorRoute = '/sayfalar/$id' | '/blog/$id' | '/bannerlar/$id' | '/e-posta-sablonlari/$id';

/** İçeriğin diğer dillerdeki hâlleri: her biri kendi düzenleyicisine bağlantı. */
export function TranslationLinks({
  items,
  to,
  stateLabel,
}: {
  items: Array<{ id: string; locale: string; state?: string }>;
  to: EditorRoute;
  stateLabel?: (state: string) => string;
}) {
  const t = useT();
  if (!items.length) return null;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-ink-3">
      <Languages className="h-4 w-4" aria-hidden />
      <span>{t('admin.translations.others')}</span>
      {items.map((it) => (
        <Link key={it.id} to={to} params={{ id: it.id }} className="text-pine hover:underline">
          {localeName(it.locale)}
          {it.state && stateLabel ? ` (${stateLabel(it.state)})` : ''}
        </Link>
      ))}
    </span>
  );
}

/**
 * "Çeviri ekle": mağazanın henüz çevirisi olmayan dillerinden birini seçtirir. Oluşturma çağrısı ve
 * sonrası (düzenleyiciye geçiş) çağırana aittir. Ek alanlar (ör. kategori adı) `children` ile verilir.
 */
export function TranslateButton({
  locales,
  current,
  existing,
  pending,
  error,
  onCreate,
  canSubmit = true,
  children,
}: {
  locales: string[];
  current: string;
  existing: string[];
  pending: boolean;
  error?: string;
  onCreate: (locale: string) => void;
  canSubmit?: boolean;
  children?: (locale: string) => ReactNode;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const missing = locales.filter((l) => l !== current && !existing.includes(l));
  const [target, setTarget] = useState('');
  if (locales.length < 2 || !missing.length) return null;
  return (
    <>
      <span className="inline-flex items-center gap-1">
        <Button
          size="sm"
          onClick={() => {
            setTarget(missing[0] ?? '');
            setOpen(true);
          }}
        >
          <Languages className="h-4 w-4" aria-hidden />
          {t('admin.translations.add')}
        </Button>
        <HelpTip k="content.translate" />
      </span>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title={t('admin.translations.add')}
        description={t('admin.translations.lead')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button onClick={() => setOpen(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              disabled={!target || pending || !canSubmit}
              onClick={() => onCreate(target)}
            >
              {t('admin.translations.create')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Field
            label={t('admin.translations.target')}
            help={<HelpTip k="content.translate_target" />}
            error={error}
          >
            {(p) => (
              <Select {...p} value={target} onChange={(e) => setTarget(e.target.value)}>
                {missing.map((l) => (
                  <option key={l} value={l}>
                    {localeName(l)}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {target && children?.(target)}
        </div>
      </Dialog>
    </>
  );
}

/**
 * Düzenleyicideki dil satırı: içeriğin dili, diğer dillerdeki hâlleri ve "Çeviri ekle". Açılan içeriğin dili
 * oturum içinde hatırlanır (listeye dönünce aynı dil seçili gelir).
 */
export function EditorLanguages({
  storeId,
  locale,
  translations,
  to,
  stateLabel,
  canTranslate,
  pending,
  error,
  onCreate,
  canSubmit,
  children,
}: {
  storeId: string;
  locale: string;
  translations: Array<{ id: string; locale: string; state?: string }>;
  to: EditorRoute;
  stateLabel?: (state: string) => string;
  canTranslate: boolean;
  pending: boolean;
  error?: string;
  onCreate: (locale: string) => void;
  canSubmit?: boolean;
  children?: (locale: string) => ReactNode;
}) {
  const t = useT();
  const { locales } = useContentLocale(storeId);
  useEffect(() => rememberLocale(storeId, locale), [storeId, locale]);
  if (locales.length < 2 && !translations.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 text-sm">
      <Badge tone="slate">{t('admin.translations.current', { lang: localeName(locale) })}</Badge>
      <TranslationLinks items={translations} to={to} stateLabel={stateLabel} />
      {canTranslate && (
        <TranslateButton
          locales={locales}
          current={locale}
          existing={translations.map((x) => x.locale)}
          pending={pending}
          error={error}
          onCreate={onCreate}
          canSubmit={canSubmit}
        >
          {children}
        </TranslateButton>
      )}
    </div>
  );
}
